import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';

const faqs = [
  {
    q: '本站提供在线播放或下载吗？',
    a: '不提供。本站是影视内容展示与推荐平台，剧集信息仅用于演示说明，所有播放、下载相关功能均不涉及。',
  },
  {
    q: '网站的数据来源是什么？',
    a: '页面展示的剧集名称、评分、简介、演职人员等信息均为虚构的演示示例，用于展示网站结构与交互，不代表任何真实作品。',
  },
  {
    q: '如何收藏喜欢的剧集？',
    a: '在剧集详情页点击“收藏”按钮，即可将剧集加入收藏。收藏记录保存在你的浏览器本地，不会上传到服务器。',
  },
  {
    q: '为什么搜索结果与预期不一致？',
    a: '搜索基于剧名、简介、类型标签与主演文本进行前端匹配。若结果较少，可尝试更换关键词或使用分类页浏览。',
  },
];

const contactItems = [
  { icon: 'ri-mail-line', label: '联系邮箱', value: 'contact@highrated-tv.example', copyable: true },
  { icon: 'ri-customer-service-2-line', label: '内容合作', value: 'media@highrated-tv.example', copyable: true },
  { icon: 'ri-time-line', label: '更新时间', value: '每日 08:00 / 20:00 内容同步', copyable: false },
  { icon: 'ri-file-list-3-line', label: '备案信息', value: '演示备案 0000000 号', copyable: false },
];

export default function AboutPage() {
  const [copied, setCopied] = useState('');

  useDocumentMeta({
    title: '关于我们 - 高评分电视剧',
    description:
      '了解高评分电视剧网站的定位、内容范围与联系方式。我们专注于汇集高评价电视剧资源，帮助用户快速找到优质影视内容。',
    keywords: '关于高评分电视剧,电视剧推荐平台,影视内容站',
  });

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(''), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copyText = async (value: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(value);
        setCopied(value);
      }
    } catch {
      setCopied('');
    }
  };

  return (
    <div className="px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: '关于我们' }]} />

      <section className="mt-6 rounded-lg overflow-hidden bg-primary-900 relative">
        <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full bg-accent-500/20"></div>
        <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-primary-700/40"></div>
        <div className="relative px-6 py-10 md:px-12 md:py-14 max-w-3xl">
          <span className="text-xs font-medium px-3 py-1 rounded-full bg-accent-500 text-background-50">品牌故事</span>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mt-4">
            高评分电视剧
          </h1>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-background-100/90">
            我们相信，好剧值得被看见。高评分电视剧网站致力于汇集最新热门、高评价的电视剧资源，
            覆盖国产剧、海外剧、古装剧、悬疑剧等多类别，以评分、口碑与内容质量为核心，帮助用户快速找到优质影视内容。
          </p>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-background-100/90">
            我们不做冗杂的信息堆砌，而是希望用清晰的结构、真诚的推荐，让每一次打开都值得。
          </p>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">内容范围说明</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: 'ri-movie-2-line', title: '多类别覆盖', desc: '国产剧、欧美剧、日韩剧及古装、悬疑、科幻等多种题材。' },
            { icon: 'ri-star-line', title: '评分导向', desc: '以评分与口碑为核心筛选标准，优先推荐高品质剧集。' },
            { icon: 'ri-layout-grid-line', title: '结构清晰', desc: '分类、筛选、排序与相关推荐，帮助你高效定位内容。' },
            { icon: 'ri-shield-check-line', title: '纯内容展示', desc: '仅提供剧集信息展示，不涉及任何播放与下载功能。' },
          ].map((item) => (
            <div key={item.title} className="p-5 rounded-lg bg-background-50 border border-background-200">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-3">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="font-heading font-semibold text-foreground-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">联系方式</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {contactItems.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-4 p-5 rounded-lg bg-background-50 border border-background-200"
            >
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 shrink-0">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <div className="flex-1 min-w-0">
                <span className="text-xs text-foreground-500">{item.label}</span>
                {item.copyable ? (
                  <a
                    href={`mailto:${item.value}`}
                    className="block text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer truncate"
                  >
                    {item.value}
                  </a>
                ) : (
                  <p className="text-sm font-medium text-foreground-900 truncate">{item.value}</p>
                )}
              </div>
              {item.copyable && (
                <button
                  type="button"
                  onClick={() => copyText(item.value)}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={copied === item.value ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {copied === item.value ? '已复制' : '复制'}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">常见问题</h2>
        <div className="mt-5 space-y-3">
          {faqs.map((faq) => (
            <div key={faq.q} className="p-5 rounded-lg bg-background-100 border border-background-200">
              <h3 className="flex items-start gap-2 font-medium text-foreground-900">
                <i className="ri-question-line text-accent-500 mt-0.5"></i>
                {faq.q}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600 pl-6">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-lg bg-background-100 border border-background-200">
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">准备好开始追剧了吗？</h2>
          <p className="text-sm text-foreground-600 mt-1">从高评分剧集中挑一部，开启你的下一段故事。</p>
        </div>
        <Link
          to="/category/guochan"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-play-circle-line text-lg"></i>
          浏览剧集
        </Link>
      </section>
    </div>
  );
}